import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ExternalLink, Loader2, Pin, RotateCcw, Search, Trash2, X } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type DriveFile, type Me, type Note, type Person, type Status } from '../api.ts';
import { useDashboard, useSave, useTaskDrawer } from '../data.ts';
import { ago, firstName, fmtWhen, priorityRank, statusLabel } from '../util.ts';
import { Gantt } from '../components/Gantt.tsx';
import { Avatar, CommentCount, DateText, Empty, Err, StatusPill, OpsMark, PriorityFlag } from '../components/ui.tsx';
import { AuthorizeGoogle } from '../components/AuthorizeGoogle.tsx';
import { NotificationsToggle } from '../components/Mobile.tsx';

export function TimelinePage() {
  const d = useDashboard();
  const { open } = useTaskDrawer();
  const save = useSave();
  if (!d.data) return <div className="page"><Err e={d.error} /></div>;
  const live = d.data.tasks.filter((t) => t.status !== 'cancelled');
  const undated = live.filter((t) => !t.start_date && !t.end_date && t.status !== 'done');
  return (
    <div className="page">
      <header className="page-h row">
        <div><h1>Timeline</h1><p className="muted">Drag a bar to move it; everything that depends on it moves too. ✦ marks bars Ops moved, with the reason on hover.</p></div>
        <span className="spacer" />
        <BaselineButton undated={undated.length} />
      </header>
      <section className="card flush"><Gantt tasks={live} projects={d.data.projects} height={720} emptyAction={<BaselineButton undated={undated.length} inline />} /></section>
      <section className="card">
        <div className="card-h"><h2>Not yet scheduled ({undated.length})</h2></div>
        {undated.length ? <ul className="tlist">{undated.map((t) => (
          <li key={t.id}><button className="link" onClick={() => open(t.id)}>{t.title}</button>
            <span className="muted sm">{d.data!.projects.find((p) => p.id === t.project)?.key} · {firstName(t.owner, d.data!.people)}</span>
            <input type="date" aria-label={`Target date for ${t.title}`} onChange={(e) => e.target.value && save.mutate({ path: `/tasks/${t.id}`, method: 'PATCH', body: { date_basis: 'target', end_date: e.target.value } })} /></li>
        ))}</ul> : <Empty>Everything open has a date.</Empty>}
      </section>
    </div>
  );
}

// Gemini drafts estimate dates for undated work, respecting dependencies. One note, one undo.
export function BaselineButton({ undated, inline = false }: { undated: number; inline?: boolean }) {
  const qc = useQueryClient();
  const run = useMutation({ mutationFn: () => api<{ note: Note }>('/agent/baseline', { method: 'POST' }), onSuccess: () => qc.invalidateQueries() });
  const undo = useMutation({ mutationFn: (id: string) => api(`/notes/${id}/undo`, { method: 'POST' }), onSuccess: () => qc.invalidateQueries() });
  const note = run.data?.note;
  if (run.isPending) return <span className="omni-working"><Loader2 size={14} className="spin" aria-hidden /> Ops is drafting a baseline from dependencies and durations…</span>;
  if (note && !undo.data) return (
    <span className="row sm">
      {note.status === 'done' ? <span>{note.summary || 'Baseline drafted as estimates.'}</span> : <span className="err">{note.error}</span>}
      {note.status === 'done' && <button className="btn sm" onClick={() => undo.mutate(note.id)}><RotateCcw size={13} aria-hidden /> Undo baseline</button>}
    </span>
  );
  if (!undated && !inline) return null;
  return (
    <button className={`btn ${inline ? 'sm' : 'primary'}`} onClick={() => run.mutate()} disabled={!undated}>
      <OpsMark size={14} aria-hidden /> Have Ops draft a baseline schedule
      {!inline && <span className="muted sm" style={{ color: 'inherit', opacity: .8 }}>· {undated} undated</span>}
    </button>
  );
}

export function MyWorkPage({ me }: { me: Me }) {
  const d = useDashboard();
  const { open } = useTaskDrawer();
  const save = useSave();
  const [who, setWho] = useState(me.email);
  if (!d.data) return <div className="page"><Err e={d.error} /></div>;
  const mine = d.data.tasks.filter((t) => t.owner === who && t.status !== 'cancelled');
  const cols: Status[] = ['planned', 'active', 'blocked', 'done'];
  return (
    <div className="page">
      <header className="page-h row"><h1>{who === me.email ? 'My work' : `${firstName(who, d.data.people)}'s work`}</h1><span className="spacer" />
        <div className="seg">{d.data.people.map((p) => <button key={p.email} className={who === p.email ? 'on' : ''} onClick={() => setWho(p.email)}>{p.email === me.email ? 'Me' : firstName(p.email, d.data!.people)}</button>)}</div></header>
      <div className="kanban">
        {cols.map((s) => {
          const list = mine.filter((t) => t.status === s && (s !== 'done' || (t.done_on ?? '') > new Date(Date.now() - 14 * 86400_000).toISOString().slice(0, 10)))
            .sort((a, b) => priorityRank(a.priority) - priorityRank(b.priority) || (a.end_date ?? '9999').localeCompare(b.end_date ?? '9999'));
          return (
            <section key={s} className="col"><h2>{statusLabel[s]} <span className="count">{list.length}</span></h2>
              {list.map((t) => (
                <article key={t.id} className="kcard">
                  <button className="link strong" onClick={() => open(t.id)}>{t.title}</button>
                  <div className="row sm"><PriorityFlag p={t.priority} onChange={(priority) => save.mutate({ path: `/tasks/${t.id}`, method: 'PATCH', body: { priority, expected_version: t.version } })} /><span className="muted">{d.data!.projects.find((p) => p.id === t.project)?.key}</span><DateText t={t} />{t.last_updated_by_agent && <OpsMark size={12} className="agent-ic" aria-label="Changed by Ops" />}<CommentCount n={t.comment_count} /></div>
                  <select aria-label="Status" value={t.status} onChange={(e) => save.mutate({ path: `/tasks/${t.id}`, method: 'PATCH', body: { status: e.target.value, expected_version: t.version } })}>
                    {Object.entries(statusLabel).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                </article>
              ))}
              {!list.length && <Empty>—</Empty>}
            </section>
          );
        })}
      </div>
    </div>
  );
}

export function NotesPage() {
  const d = useDashboard();
  const [q, setQ] = useState(''); const [term, setTerm] = useState('');
  const notes = useQuery({ queryKey: ['notes', term], queryFn: () => api<{ notes: Note[] }>(`/notes${term ? `?q=${encodeURIComponent(term)}` : ''}`) });
  const save = useSave();
  const [openId, setOpen] = useState<string | null>(null);
  const people = d.data?.people ?? [];
  return (
    <div className="page">
      <header className="page-h"><h1>Notes log</h1><p className="muted">Every brain dump, and exactly what Ops did with it. Undo a whole note's changes, or delete ideas that didn't pan out.</p></header>
      <form className="row" onSubmit={(e) => { e.preventDefault(); setTerm(q.trim()); }}>
        <div className="search"><Search size={14} aria-hidden /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes: “patent attorney”, “EMS”…" aria-label="Search notes" /></div>
        <button className="btn">Search</button>
      </form>
      <Err e={notes.error ?? save.error} />
      <ul className="notes">
        {(notes.data?.notes ?? []).map((n) => (
          <li key={n.id} className={`note st-${n.status}${n.reverted ? ' reverted' : ''}`}>
            <div className="note-h"><Avatar email={n.author} people={people} size={22} /><b>{firstName(n.author, people)}</b><span className="muted sm">{fmtWhen(n.created_at)}</span>
              <span className={`pill n-${n.status}`}>{n.reverted ? 'Undone' : n.status === 'done' ? 'Acted on' : n.status === 'saved' ? 'Saved' : n.status === 'error' ? 'Failed' : 'Working'}</span>
              <span className="spacer" />
              {n.status === 'done' && !n.reverted && <button className="btn sm" onClick={() => save.mutate({ path: `/notes/${n.id}/undo` })}><RotateCcw size={13} aria-hidden /> Undo changes</button>}
              {(n.status === 'error' || n.status === 'saved') && <button className="btn sm" onClick={() => save.mutate({ path: `/notes/${n.id}/retry` })}><OpsMark size={13} aria-hidden /> Let Ops act</button>}
              <button className="btn sm ghost" aria-label="Delete note" onClick={() => confirm('Delete this note? Changes it already made stay unless you undo them first.') && save.mutate({ path: `/notes/${n.id}`, method: 'DELETE' })}><Trash2 size={13} /></button>
            </div>
            <p className="note-text">{n.text}</p>
            {(n.attachments?.length ?? 0) > 0 && (
              <div className="note-attachments">
                {n.attachments!.map((at, i) => (
                  <a key={i} href={`/api/notes/${n.id}/attachments/${i}`} target="_blank" rel="noreferrer" title={`${at.name} · ${(at.size / 1024).toFixed(0)} KB`}>
                    {at.thumb ? <img src={at.thumb} alt={at.name} /> : <span className="pill">{at.mimeType === 'application/pdf' ? 'PDF' : 'Image'}</span>} {at.name}
                  </a>
                ))}
              </div>
            )}
            {n.summary && <p className="note-sum"><OpsMark size={13} aria-hidden /> {n.summary}</p>}
            {n.error && <p className="err sm">{n.error}</p>}
            {(n.actions?.length ?? 0) > 0 && (
              <>
                <button className="link sm" onClick={() => setOpen(openId === n.id ? null : n.id)}><ChevronDown size={12} aria-hidden /> {n.actions!.length} steps{n.model ? ` · ${n.model}` : ''}</button>
                {openId === n.id && <ol className="steps">{n.actions!.map((a, i) => <li key={i} className={a.ok ? '' : 'failed'}><code>{a.tool}</code> {JSON.stringify(a.args).slice(0, 160)}</li>)}</ol>}
              </>
            )}
          </li>
        ))}
        {notes.data && !notes.data.notes.length && <Empty>{term ? 'No notes match.' : 'No notes yet. Press ⌘K.'}</Empty>}
      </ul>
    </div>
  );
}

// Morning digest: your briefings, inbox proposals and due tasks, emailed from your Gmail to you at the hour you pick.
function DigestSettings() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['digest'], queryFn: () => api<{ settings: { enabled: boolean; hour: number; tz: string }; last_sent: string | null; can_send: boolean }>('/digest') });
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const save = useMutation({ mutationFn: (b: { enabled: boolean; hour: number }) => api('/digest', { method: 'PUT', body: { ...b, tz } }), onSuccess: () => qc.invalidateQueries({ queryKey: ['digest'] }) });
  const test = useMutation({ mutationFn: () => api<{ sent: boolean; reason?: string; count: number; subject: string }>('/digest/test', { method: 'POST' }) });
  const d = q.data;
  if (!d) return null;
  const s = d.settings;
  const hour = (h: number) => new Date(2026, 0, 1, h).toLocaleTimeString('en-US', { hour: 'numeric' });
  return (
    <>
    <section className="card"><div className="card-h"><h2>Push notifications</h2></div><NotificationsToggle /></section>
    <section className="card">
      <div className="card-h"><h2>Morning digest email</h2><span className="muted sm">Sent from your Gmail to you. Skipped on days with nothing new.</span></div>
      {!d.can_send ? <p className="notice">To email you a digest, Operations needs permission to send from your Gmail. <AuthorizeGoogle back="/workspace" label="Authorize Gmail sending" /></p> : (
        <div className="row">
          <label className="row sm"><input type="checkbox" checked={s.enabled} onChange={(e) => save.mutate({ enabled: e.target.checked, hour: s.hour ?? 7 })} /> Email me each morning at</label>
          <select value={s.hour ?? 7} aria-label="Digest hour" onChange={(e) => save.mutate({ enabled: s.enabled, hour: Number(e.target.value) })}>
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hour(h)}</option>)}</select>
          <span className="muted sm">{s.tz || tz}{d.last_sent ? ` · last sent ${d.last_sent}` : ''}</span>
          <span className="spacer" />
          <button className="btn sm" onClick={() => test.mutate()} disabled={test.isPending}>{test.isPending ? 'Sending…' : 'Send me one now'}</button>
        </div>
      )}
      {test.data && <p className="sm muted">{test.data.sent ? `Sent “${test.data.subject}”.` : `Not sent: ${test.data.reason}.`}</p>}
      <Err e={save.error ?? test.error} />
    </section>
    </>
  );
}

// Apps connected to Operations over OAuth (Claude). Each person sees and disconnects their own.
function ConnectedApps() {
  const q = useQuery({ queryKey: ['connected-apps'], queryFn: () => api<{ apps: { id: string; name: string; host: string; connected_at: string; last_used_at: string | null }[]; mcp_url: string }>('/connected-apps') });
  const save = useSave();
  const [copied, setCopied] = useState(false);
  if (!q.data) return null;
  const copy = () => { navigator.clipboard?.writeText(q.data.mcp_url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }, () => {}); };
  return (
    <section className="card">
      <div className="card-h"><h2>Connected apps</h2><span className="muted sm">Use Ops from Claude: add a custom connector with <code>{q.data.mcp_url}</code> <button className="link sm" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button></span></div>
      {q.data.apps.length ? <ul className="tlist">{q.data.apps.map((a) => (
        <li key={a.id} className="row"><b>{a.name}</b><span className="muted sm">{a.host} · connected {fmtWhen(a.connected_at)}{a.last_used_at ? ` · last used ${ago(a.last_used_at)}` : ''}</span><span className="spacer" />
          <button className="btn sm" onClick={() => confirm(`Disconnect ${a.name}? It loses access right away.`) && save.mutate({ path: `/connected-apps/${a.id}`, method: 'DELETE' })}>Disconnect</button></li>
      ))}</ul> : <p className="muted sm">Nothing connected. Changes a connected app makes show in the Notes log, where you can undo them.</p>}
      <Err e={save.error} />
    </section>
  );
}

export function WorkspacePage() {
  const status = useQuery({ queryKey: ['ws-status'], queryFn: () => api<{ connected: boolean; email: string; scopes: string[]; error: string }>('/workspace/status') });
  const save = useSave();
  const [tab, setTab] = useState<'recent' | 'drives' | 'search'>('recent');
  const [q, setQ] = useState(''); const [term, setTerm] = useState('');
  const [path, setPath] = useState<{ id: string; name: string }[]>([]);
  const [preview, setPreview] = useState<DriveFile | null>(null);
  const drives = useQuery({ queryKey: ['drives'], queryFn: () => api<{ drives: { id: string; name: string }[] }>('/workspace/drives'), enabled: tab === 'drives' && !!status.data?.connected });
  const here = path.at(-1);
  const query = tab === 'recent' ? 'recent=1' : tab === 'search' ? (term ? `q=${encodeURIComponent(term)}` : '') : here ? `folder=${encodeURIComponent(here.id)}` : '';
  const files = useQuery({ queryKey: ['drive', query], queryFn: () => api<{ files: DriveFile[] }>(`/workspace/files?${query}`), enabled: !!query && !!status.data?.connected, retry: false });
  const scopes = status.data?.scopes ?? [];
  const missing = [scopes.includes('https://www.googleapis.com/auth/gmail.readonly') ? '' : 'Gmail (read)', scopes.includes('https://www.googleapis.com/auth/contacts.readonly') ? '' : 'Contacts (read)',
    scopes.includes('https://www.googleapis.com/auth/calendar.events') ? '' : 'Calendar holds', scopes.includes('https://www.googleapis.com/auth/gmail.send') ? '' : 'Gmail (send your digest)'].filter(Boolean);

  return (
    <div className="page">
      <header className="page-h row"><div><h1>Workspace</h1><p className="muted">Your Drive, read as you; Google's sharing decides what you see. Files open and edit in Google.</p></div><span className="spacer" />
        {status.data?.connected ? <div className="row"><span className="pill s-done">Connected · {status.data.email}</span><button className="btn sm" onClick={() => save.mutate({ path: '/workspace/disconnect' })}>Disconnect</button></div>
          : <a className="btn primary" href="/auth/workspace/connect">Connect Google Workspace</a>}</header>
      <ConnectedApps />
      {status.data?.connected && <DigestSettings />}
      {status.data?.connected && missing.length > 0 && <p className="notice">Ops can't use {missing.join(', ')} yet. <AuthorizeGoogle back="/workspace" /> and approve them on Google's screen.</p>}
      {status.data?.connected && (
        <>
          <div className="seg">{(['recent', 'drives', 'search'] as const).map((t) => <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>{{ recent: 'Recent', drives: 'Shared drives', search: 'Search' }[t]}</button>)}</div>
          {tab === 'search' && <form className="row" onSubmit={(e) => { e.preventDefault(); setTerm(q.trim()); }}><div className="search"><Search size={14} aria-hidden /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search names and contents…" aria-label="Search Drive" /></div><button className="btn">Search</button></form>}
          {tab === 'drives' && (
            <nav className="crumbs"><button className="link" onClick={() => setPath([])}>All drives</button><button className="link" onClick={() => setPath([{ id: 'root', name: 'My Drive' }])}>My Drive</button>
              {path.map((p, i) => <span key={p.id}> / <button className="link" onClick={() => setPath(path.slice(0, i + 1))}>{p.name}</button></span>)}</nav>
          )}
          <div className={`files${preview ? ' with-preview' : ''}`}>
            <ul className="card ftable">
              {tab === 'drives' && !here && (drives.data?.drives ?? []).map((dr) => <li key={dr.id}><span>🗂</span><button className="link" onClick={() => setPath([{ id: dr.id, name: dr.name }])}>{dr.name}</button></li>)}
              {(files.data?.files ?? []).map((f) => {
                const folder = f.mimeType === 'application/vnd.google-apps.folder';
                return (
                  <li key={f.id} className={preview?.id === f.id ? 'on' : ''}>
                    <img src={f.iconLink} alt="" width={16} height={16} />
                    {folder && tab === 'drives' ? <button className="link" onClick={() => setPath([...path, { id: f.id, name: f.name }])}>{f.name}</button> : <a href={f.webViewLink} target="_blank" rel="noreferrer">{f.name}</a>}
                    <span className="muted sm">{f.lastModifyingUser?.displayName ?? ''} · {ago(f.modifiedTime)}</span>
                    <span className="row">
                      {!folder && <button className="btn sm" onClick={() => setPreview(preview?.id === f.id ? null : f)}>Preview</button>}
                      <button className="btn sm" onClick={() => save.mutate({ path: '/resources', body: { title: f.name, url: f.webViewLink, drive_file_id: f.id } })}><Pin size={12} aria-hidden /> Pin</button>
                    </span>
                  </li>
                );
              })}
              {files.data && !files.data.files.length && <li className="muted">Nothing here.</li>}
            </ul>
            {preview && <div className="card preview"><div className="row"><b className="clamp1">{preview.name}</b><span className="spacer" /><a className="btn sm" href={preview.webViewLink} target="_blank" rel="noreferrer"><ExternalLink size={12} aria-hidden /> Open</a><button className="btn sm ghost" onClick={() => setPreview(null)}>Close</button></div>
              <iframe title={preview.name} src={`https://drive.google.com/file/d/${preview.id}/preview`} /></div>}
          </div>
        </>
      )}
    </div>
  );
}

type Contact = { id: string; name: string; organization: string; role: string; workstream: string | null; email: string; phone: string; notes: string;
  source: string; source_url: string; created_by: string };
type Lookup = { found: boolean; card: { full_name: string; email: string; phone: string; title: string; organization: string } | null;
  source: { type: string; label: string; url: string } | null; checked: string[]; errors: string[] };
type Sources = { connected: boolean; gmail: boolean; contacts: boolean; drive: boolean };

// What a lookup would change: only fields that are empty or different, so applying never wipes something typed by hand.
const proposed = (c: Partial<Contact>, r: Lookup) => {
  if (!r.card) return {};
  const out: Partial<Contact> = {};
  if (r.card.full_name && r.card.full_name.length > (c.name ?? '').length && r.card.full_name.toLowerCase().startsWith((c.name ?? '').toLowerCase().split(' ')[0])) out.name = r.card.full_name;
  if (r.card.email && r.card.email !== c.email) out.email = r.card.email;
  if (r.card.phone && !c.phone) out.phone = r.card.phone;
  if (r.card.title && !c.role) out.role = r.card.title;
  if (r.card.organization && !c.organization) out.organization = r.card.organization;
  if (r.source) { out.source = r.source.label; out.source_url = r.source.url; }
  return out;
};

function LookupResult({ r, change, onApply, onDismiss, busy }: { r: Lookup; change: Partial<Contact>; onApply: () => void; onDismiss: () => void; busy?: boolean }) {
  const keys = (['name', 'email', 'phone', 'role', 'organization'] as const).filter((k) => change[k]);
  return (
    <div className="lookup">
      {r.found && r.card ? (
        <>
          <span><b>{r.card.full_name}</b>{r.card.email && <> · <code>{r.card.email}</code></>}{r.card.phone && <> · {r.card.phone}</>}{r.card.title && <> · {r.card.title}</>}{r.card.organization && <> · {r.card.organization}</>}
            {r.source && <> <a className="muted sm" href={r.source.url} target="_blank" rel="noreferrer">({r.source.label})</a></>}</span>
          {keys.length ? <button className="btn sm primary" onClick={onApply} disabled={busy}>Fill {keys.join(', ')}</button> : <span className="muted sm">Already up to date.</span>}
        </>
      ) : <span className="muted sm">No match in {r.checked.join(', ') || 'Google'}.{r.errors.length > 0 && <span className="err"> {r.errors.join(' · ')}</span>}</span>}
      <button className="icon" aria-label="Dismiss" onClick={onDismiss}><X size={13} /></button>
    </div>
  );
}

function ContactsDirectory() {
  const q = useQuery({ queryKey: ['contacts'], queryFn: () => api<{ contacts: Contact[]; google: Sources }>('/contacts') });
  const d = useDashboard();
  const save = useSave();
  const blankDraft = { name: '', organization: '', role: '', workstream: '', email: '', phone: '', source: '', source_url: '' };
  const [draft, setDraft] = useState(blankDraft);
  const [found, setFound] = useState<Record<string, Lookup>>({});
  const [busy, setBusy] = useState('');
  const projects = d.data?.projects ?? [];
  const g = q.data?.google;
  const canLook = !!g && (g.gmail || g.contacts);
  const look = async (key: string, name: string, organization: string) => {
    setBusy(key);
    try { const r = await api<Lookup>('/contacts/lookup', { method: 'POST', body: { name, organization } }); setFound((f) => ({ ...f, [key]: r })); }
    catch (e) { setFound((f) => ({ ...f, [key]: { found: false, card: null, source: null, checked: [], errors: [(e as Error).message] } })); }
    finally { setBusy(''); }
  };
  const drop = (key: string) => setFound(({ [key]: _, ...rest }) => rest);
  const field = (c: Contact, k: keyof Contact, w: number) => (
    <input key={`${c.id}-${k}-${c[k] ?? ''}`} defaultValue={(c[k] as string) ?? ''} style={{ width: w }} aria-label={`${k} for ${c.name}`}
      onBlur={(e) => { if (e.target.value !== (c[k] ?? '')) save.mutate({ path: `/contacts/${c.id}`, method: 'PATCH', body: { [k]: e.target.value } }); }} />
  );
  return (
    <section className="card">
      <div className="card-h"><h2>External partners &amp; vendors</h2><span className="muted sm">⌘K uses this to know who people are without asking.</span></div>
      {g && (!g.connected || !g.gmail || !g.contacts) && (
        <p className="notice">{!g.connected ? 'Connect Google Workspace to look people up in Gmail and Contacts.' : `Lookup can't read ${[!g.gmail && 'Gmail', !g.contacts && 'Google Contacts'].filter(Boolean).join(' or ')} yet.`}{' '}
          <AuthorizeGoogle back="/people" label={g.connected ? 'Authorize Gmail & Contacts access' : 'Connect Google Workspace'} /></p>
      )}
      <table className="table"><thead><tr><th>Name</th><th>Organization</th><th>Role / title</th><th>Email</th><th>Phone</th><th>Workstream</th><th>Notes</th><th /></tr></thead>
        <tbody>{(q.data?.contacts ?? []).map((c) => (
          <Fragment key={c.id}>
            <tr>
              <td>{field(c, 'name', 120)}</td><td>{field(c, 'organization', 130)}</td><td>{field(c, 'role', 120)}</td><td>{field(c, 'email', 170)}</td><td>{field(c, 'phone', 110)}</td>
              <td><select value={c.workstream ?? ''} aria-label={`Workstream for ${c.name}`} onChange={(e) => save.mutate({ path: `/contacts/${c.id}`, method: 'PATCH', body: { workstream: e.target.value || null } })}>
                <option value="">—</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.key}</option>)}</select></td>
              <td>{field(c, 'notes', 160)}</td>
              <td className="row nowrap">
                {canLook && <button className="btn sm" title={c.source ? `Last filled from ${c.source}` : 'Look up email, phone and title in Gmail, Google Contacts and Drive'}
                  onClick={() => look(c.id, c.name, c.organization)} disabled={!!busy}>{busy === c.id ? <Loader2 size={13} className="spin" aria-hidden /> : <Search size={13} aria-hidden />} Enrich</button>}
                <button className="icon" aria-label={`Remove ${c.name}`} onClick={() => confirm(`Remove ${c.name}?`) && save.mutate({ path: `/contacts/${c.id}`, method: 'DELETE' })}><Trash2 size={13} /></button>
              </td>
            </tr>
            {found[c.id] && <tr className="lookup-row"><td colSpan={8}>
              <LookupResult r={found[c.id]} change={proposed(c, found[c.id])} busy={save.isPending} onDismiss={() => drop(c.id)}
                onApply={async () => { await save.mutateAsync({ path: `/contacts/${c.id}`, method: 'PATCH', body: proposed(c, found[c.id]) }); drop(c.id); }} />
            </td></tr>}
          </Fragment>
        ))}</tbody></table>
      {q.data && !q.data.contacts.length && <Empty>No external contacts yet. ⌘K offers to add new people it hears about, or add one here.</Empty>}
      <form className="row" onSubmit={async (e) => { e.preventDefault(); await save.mutateAsync({ path: '/contacts', body: { ...draft, workstream: draft.workstream || null } }); setDraft(blankDraft); drop('new'); }}>
        <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name" aria-label="Contact name" required />
        <input value={draft.organization} onChange={(e) => setDraft({ ...draft, organization: e.target.value })} placeholder="Organization" aria-label="Organization" />
        <input value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} placeholder="Role / title" aria-label="Role" />
        <input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="Email" aria-label="Email" />
        <input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="Phone" aria-label="Phone" style={{ width: 120 }} />
        <select value={draft.workstream} onChange={(e) => setDraft({ ...draft, workstream: e.target.value })} aria-label="Workstream"><option value="">Workstream</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.key}</option>)}</select>
        {canLook && <button type="button" className="btn" disabled={!draft.name.trim() || !!busy} onClick={() => look('new', draft.name, draft.organization)}>
          {busy === 'new' ? <Loader2 size={13} className="spin" aria-hidden /> : <Search size={13} aria-hidden />} Look up</button>}
        <button className="btn">Add contact</button>
      </form>
      {found.new && <LookupResult r={found.new} change={proposed(draft, found.new)} onDismiss={() => drop('new')}
        onApply={() => { setDraft({ ...draft, ...(proposed(draft, found.new) as any) }); drop('new'); }} />}
      <Err e={save.error} />
    </section>
  );
}

export function PeoplePage({ me }: { me: Me }) {
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<{ users: Person[] }>('/users') });
  const save = useSave();
  const [email, setEmail] = useState(''); const [name, setName] = useState('');
  const people = (users.data?.users ?? []) as any[];
  return (
    <div className="page">
      <header className="page-h"><h1>People</h1><p className="muted">Only these @gbsoft.dev Google accounts can sign in. Everyone is an equal collaborator.</p></header>
      <section className="card">
        <table className="table"><thead><tr><th /><th>Name</th><th>Account</th><th>Last active</th><th /></tr></thead>
          <tbody>{people.map((u) => (
            <tr key={u.email} className={u.active ? '' : 'off'}><td><Avatar email={u.email} people={people} /></td><td>{u.name || '—'}</td><td>{u.email}</td><td title={u.last_login_at ? `Signed in ${fmtWhen(u.last_login_at)}` : undefined}>{(u.last_active_at ?? u.last_login_at) ? ago((u.last_active_at ?? u.last_login_at)!) : 'Never'}</td>
              <td>{u.email !== me.email && <button className="btn sm" onClick={() => save.mutate({ path: `/users/${encodeURIComponent(u.email)}`, method: 'PATCH', body: { active: !u.active } })}>{u.active ? 'Deactivate' : 'Reactivate'}</button>}</td></tr>
          ))}</tbody></table>
        <form className="row" onSubmit={async (e) => { e.preventDefault(); await save.mutateAsync({ path: '/users', body: { email, name } }); setEmail(''); setName(''); }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" aria-label="Name" />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@gbsoft.dev" aria-label="Email" required />
          <button className="btn primary">Grant access</button>
        </form>
        <Err e={save.error} />
      </section>
      <ContactsDirectory />
    </div>
  );
}
